<script setup>
import {Dialog, DialogPanel, TransitionChild, TransitionRoot} from "@headlessui/vue";
import {defineProps, defineEmits, reactive, onMounted} from "vue";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { subscriptionApi } from "@/Api/subscription-api";
import {useToast} from "vue-toastification";

const discordUrl = import.meta.env.VITE_DISCORD_URL
const toast = useToast();
const emits = defineEmits(['close']);

const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
    discordUrl: {
        type: String,
        default: '#',
    }
});

const data = reactive({
    subscriptionName: null,
    campaignMinutes: null,
    credits: null,
});

function requestRecentSubscription() {
    subscriptionApi.getRecentSubscription().then((response) => {
        data.subscriptionName = response.data.subscription_name;
        data.campaignMinutes = response.data.campaign_minutes_reward;
        data.credits = response.data.credits_reward;
    }).catch(function (error) {
        const message = error.response?.data?.message ?? "Something went wrong. Please try again.";
        toast.error(message);
    });
}

const closeModal = () => {
    emits('close');
};


onMounted(async () => {
    requestRecentSubscription();
});

</script>

<template>
    <TransitionRoot as="template" :show="props.show">
        <Dialog as="div" class="relative z-10" @close="closeModal">
            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                <div class="fixed inset-0 bg-gray-900 bg-opacity-90 transition-opacity" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div class="flex min-h-full items-start justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel class="w-full sm:w-9/12 max-w-3xl bg-gray-750 rounded-lg shadow-lg">
                        <div class="text-left text-white p-4 border-b border-gray-700">
                            <div class="flex justify-between items-center">
                                <p class="text-gray-300 font-semibold text-2xl">Thank You!</p>
                                <FontAwesomeIcon
                                    :icon="['fas', 'xmark']"
                                    class="mt-1 mr-2 cursor-pointer"
                                    size="lg"
                                    style="color: #ffffff;"
                                    @click="closeModal"
                                />
                            </div>
                        </div>
                        <div class="text-left text-white p-4 bg-gray-800 rounded-b-lg shadow-md">
                            <div v-if="data.subscriptionName !== null" class="mb-2">
                                <div class="text-green-500 text-2xl">
                                    <strong >{{ data.subscriptionName }}</strong>
                                </div>
                            </div>
                            <p class="text-lg font-semibold mb-4 leading-snug">
                                Thank you for being a part of our community and supporting this project. We hope you enjoy your rewards!
                            </p>
                            <div class="space-y-4 mt-2 text-base leading-snug">
                                <p v-if="data.credits !== null">
                                    <strong>Workshop Credits:</strong> <span class="text-blue-500 font-bold">{{ data.credits }}</span> credits have been added to your account.
                                </p>
                                <p v-if="data.campaignMinutes !== null && data.campaignMinutes > 0">
                                    <strong>Campaign Minutes:</strong> <span class="text-orange-500 font-bold">{{ data.campaignMinutes }}</span> campaign minutes have been added to your account.
                                </p>
                            </div>
                            <p class="text-sm text-gray-300 mt-4">
                                For more details and account management options, please visit your
                                <router-link to="/account" class="underline" @click.native="closeModal">Account Page</router-link>.
                            </p>

                            <div class="flex items-center justify-between text-sm">
                                <p class="text-gray-300">
                                    Join our Discord community to connect with others and stay updated on the latest news and features.
                                </p>
                                <a :href="discordUrl" class="flex items-center ml-2 -mb-12 xs:mb-1">
                                    <FontAwesomeIcon :icon="['fa-brands', 'fa-discord']" size="2x" color="white"/>
                                </a>
                            </div>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<style scoped>
/* You can add your styles here */
</style>
